// Feature-flag registry — single source of truth for the admin Launch page.
//
// One entry per gateable surface. Pure module — no DB, no runtime deps. The
// worker's services/feature-flags.ts imports this to know the keys, defaults,
// and recommended visibilities per phase. The admin SPA imports the same
// const to render labels, descriptions, and category groupings.
//
// Adding a flag: append a new entry to FEATURE_FLAGS below. Then either
// gate a call site against `isFeatureEnabled` (set `status: 'shipped'`) or
// leave it forward-looking (`status: 'stubbed'` — the toggle exists in the
// admin UI so Richie can plan ahead, but flipping it has no runtime effect
// yet).

import type { ActiveHat } from '../capabilities.js';

/**
 * Visibility of a feature flag. The simple cases are `off`, `all`,
 * `founding_cohort`. SA-014 adds a parametric canary form
 * `canary_<N>_pct` (where N is 1..99) — a deterministic per-user hash bucket
 * lets the flag light up for ~N% of distinct users without an admin manually
 * picking who's in.
 *
 * Canary kicks in BEFORE `founding_cohort` and `all` in the decision logic
 * (see `isFeatureEnabled` in apps/web/src/worker/services/feature-flags.ts).
 * Anonymous callers are never in the canary — there's no stable id to bucket
 * them by.
 */
export type FeatureVisibilitySimple = 'off' | 'founding_cohort' | 'all';
export type FeatureVisibilityCanary = `canary_${number}_pct`;
export type FeatureVisibility = FeatureVisibilitySimple | FeatureVisibilityCanary;

/**
 * Launch phases. `phase_0` / `phase_1` / `phase_2` mirror PRD §11. `phase_waitlist`
 * is the operational stage between them: the full marketing site is live and the
 * waitlist is collecting artist + venue leads, but account creation stays closed
 * (see the `waitlist_mode` flag). Phases are ORDERED here as the platform moves
 * through them (pre-launch → waitlist → seed → expansion).
 *
 * A phase is editorial — it does not gate features directly (per-flag visibility
 * does). It drives the "recommended for this phase" hints and one-click preset in
 * the admin Launch page, plus the marketing-side phase copy.
 */
export type LaunchPhase = 'phase_0' | 'phase_waitlist' | 'phase_1' | 'phase_2';

export const LAUNCH_PHASES: readonly LaunchPhase[] = [
  'phase_0',
  'phase_waitlist',
  'phase_1',
  'phase_2',
] as const;

/**
 * Simple, non-parametric visibilities for UI affordances (radio groups, etc.).
 * The canary form is constructed dynamically rather than enumerated.
 */
export const FEATURE_VISIBILITIES_SIMPLE: readonly FeatureVisibilitySimple[] = [
  'off',
  'founding_cohort',
  'all',
] as const;

/** @deprecated — use FEATURE_VISIBILITIES_SIMPLE for UI; runtime acceptance is broader. */
export const FEATURE_VISIBILITIES: readonly FeatureVisibilitySimple[] = FEATURE_VISIBILITIES_SIMPLE;

const CANARY_PATTERN = /^canary_([1-9]\d?)_pct$/;

/**
 * Parse a canary visibility string and return its percentage (1..99) or null
 * if the input is not a well-formed canary.
 */
export function parseCanaryPct(value: string): number | null {
  const m = CANARY_PATTERN.exec(value);
  if (m === null) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n >= 1 && n <= 99 ? n : null;
}

export function isFeatureVisibility(value: unknown): value is FeatureVisibility {
  if (typeof value !== 'string') return false;
  if (value === 'off' || value === 'founding_cohort' || value === 'all') return true;
  return parseCanaryPct(value) !== null;
}

export function isLaunchPhase(value: unknown): value is LaunchPhase {
  return (
    value === 'phase_0' ||
    value === 'phase_waitlist' ||
    value === 'phase_1' ||
    value === 'phase_2'
  );
}

/** Human-readable phase metadata for the admin UI. */
export const LAUNCH_PHASE_LABELS: Record<LaunchPhase, { label: string; tagline: string }> = {
  phase_0: {
    label: 'Phase 0: Pre-launch',
    tagline: 'Trust infrastructure online; no public sign-ups, no commerce, no recommendations.',
  },
  phase_waitlist: {
    label: 'Waitlist: Collecting sign-ups',
    tagline:
      'The full marketing site is live and the waitlist is capturing artist + venue leads. Account creation stays closed until seed launch. Pairs Waitlist mode ON with Public sign-ups OFF.',
  },
  phase_1: {
    label: 'Phase 1: SE QLD Seed',
    tagline: 'Founding 50 venues across Brisbane, Sunshine Coast, and Northern Rivers.',
  },
  phase_2: {
    label: 'Phase 2: Corridor expansion',
    tagline: 'NSW North Coast onboarding; multi-corridor analytics live.',
  },
};

/** Flags grouped by operational concern for sectioning in the admin UI. */
export type FeatureCategory = 'marketing' | 'trust' | 'commerce' | 'discovery' | 'communications';

export const FEATURE_CATEGORY_LABELS: Record<FeatureCategory, string> = {
  marketing: 'Marketing & Launch',
  trust: 'Trust & Safety',
  commerce: 'Commerce',
  discovery: 'Discovery',
  communications: 'Communications',
};

/**
 * Flag definition. `recommended` is the suggested visibility *for that phase*;
 * the actual stored visibility may differ (Richie can override). `status`
 * tells the admin UI whether flipping the flag does anything yet.
 */
export type FeatureFlagDef = {
  key: string;
  label: string;
  description: string;
  category: FeatureCategory;
  recommended: Record<LaunchPhase, FeatureVisibility>;
  /**
   * Per-flag failure-mode default. When the KV+D1 read fails, isFeatureEnabled
   * returns true if this is 'all', false if 'off'. Choose 'off' for money or
   * trust paths; 'all' for marketing surfaces that shouldn't go dark on a blip.
   */
  defaultWhenUnavailable: 'off' | 'all';
  /** Shipped: turning the flag on/off changes user-visible behaviour today. */
  status: 'shipped' | 'stubbed';
  /** Plain-English note about what happens to in-flight flows when this turns off. */
  policyNote: string;
  /** Hats this flag is relevant to. Hint for the admin UI; gating is hat-agnostic. */
  relevantHats: readonly ActiveHat[];
};

/**
 * Full flag registry. Keys are stable — never rename a key without a
 * migration, because they're persisted in `platform_features.key`.
 */
export const FEATURE_FLAGS = [
  // ─── Marketing & Launch ─────────────────────────────────────────────────
  {
    // The pre-launch funnel switch. OFF by default; ON during the Waitlist
    // stage. Drives the marketing SPA's `useWaitlistMode()` — when on, every
    // account CTA (the sign-up page + hero/sticky/closing buttons that point at
    // /sign-up) invites the visitor to join the waitlist instead of hitting the
    // closed-signups notice. The hard gate on account creation is still
    // `public_signups` (server-enforced in routes/auth.ts); this flag only
    // reshapes the client funnel toward waitlist capture (routes/waitlist.ts →
    // Brevo). Recommended ON in `phase_waitlist`, OFF everywhere else.
    key: 'waitlist_mode',
    label: 'Waitlist mode',
    description:
      'When on, the marketing site invites visitors to join the waitlist instead of creating an account. The sign-up page and account CTAs lead to the waitlist capture (saved to Brevo). Turn this on during the pre-launch waitlist stage and pair it with Public sign-ups OFF; turn it off once public sign-ups open.',
    category: 'marketing',
    recommended: {
      phase_0: 'off',
      phase_waitlist: 'all',
      phase_1: 'off',
      phase_2: 'off',
    },
    defaultWhenUnavailable: 'off',
    status: 'shipped',
    policyNote:
      'Off shows the normal sign-up flow (subject to Public sign-ups). On redirects account creation to the waitlist. Existing accounts and in-flight sessions are unaffected either way.',
    relevantHats: ['artist', 'host'],
  },

  // ─── Trust & Safety ─────────────────────────────────────────────────────
  {
    key: 'public_signups',
    label: 'Public sign-ups',
    description:
      'When on, anyone can create an account at /sign-up. When off, the sign-up form is replaced with a "We\'re not accepting new sign-ups yet" empty state (or the waitlist, if Waitlist mode is on) and the API rejects new POSTs.',
    category: 'trust',
    recommended: { phase_0: 'off', phase_waitlist: 'off', phase_1: 'all', phase_2: 'all' },
    defaultWhenUnavailable: 'off',
    status: 'shipped',
    policyNote:
      'Off blocks new sign-ups. People already signed up keep their accounts; in-flight onboarding completes normally.',
    relevantHats: ['artist', 'host'],
  },
  {
    key: 'email_verification',
    label: 'Email verification required',
    description:
      'When on, new sign-ups land in a pending state, get emailed a time-limited verification link, and must click it (or resend from Settings) before they can access the app. Turning this on without `RESEND_API_KEY` bound will lock new users out — the verification email never leaves the queue, so nobody can ever complete the link.',
    category: 'trust',
    recommended: { phase_0: 'off', phase_waitlist: 'off', phase_1: 'off', phase_2: 'all' },
    defaultWhenUnavailable: 'off',
    status: 'shipped',
    policyNote:
      'Off lets new accounts sign in immediately, unverified. On gates access behind verification. In-flight unverified accounts are not retro-blocked; only new sign-ups after the flip are affected.',
    relevantHats: ['artist', 'host'],
  },
  {
    key: 'mfa_required',
    label: 'MFA required for admins',
    description:
      'When on, admin accounts must have a TOTP second factor enrolled and are blocked from /admin until they set it up in Settings → Security. Anyone with MFA enabled completes a code challenge at sign-in regardless of this flag.',
    category: 'trust',
    recommended: { phase_0: 'off', phase_waitlist: 'off', phase_1: 'all', phase_2: 'all' },
    defaultWhenUnavailable: 'off',
    status: 'shipped',
    policyNote:
      'Off lets admins sign in with password only. On enforces enrolment for admins; the sign-in TOTP/recovery challenge applies to any user who has enabled MFA either way.',
    relevantHats: ['admin'],
  },
  {
    key: 'disputes_module',
    label: 'Disputes module',
    description:
      'When on, hosts and artists can open disputes against completed exchanges; admin moderation is live. When off, new disputes cannot be opened — but in-flight disputes keep their SLA notifications and respondent flows.',
    category: 'trust',
    recommended: { phase_0: 'all', phase_waitlist: 'all', phase_1: 'all', phase_2: 'all' },
    defaultWhenUnavailable: 'all',
    status: 'shipped',
    policyNote:
      'Off blocks new dispute creation. Existing open disputes continue to resolve normally (respondents, evidence, SLA sweepers all active).',
    relevantHats: ['artist', 'host'],
  },

  // ─── Commerce ───────────────────────────────────────────────────────────
  {
    key: 'stripe_live',
    label: 'Stripe billing live',
    description:
      'When on, /billing uses real Stripe checkout and webhooks. When off, /billing shows simulate buttons in DEV and a "Billing isn\'t live yet" notice in production. State machine for subscriptions is correct in both modes.',
    category: 'commerce',
    recommended: {
      phase_0: 'off',
      phase_waitlist: 'off',
      phase_1: 'founding_cohort',
      phase_2: 'all',
    },
    defaultWhenUnavailable: 'off',
    status: 'stubbed',
    policyNote:
      'Off blocks new checkout flows. Already-active subscriptions keep their state; Stripe webhooks still process for in-flight customers.',
    relevantHats: ['host'],
  },
  {
    key: 'premium_subscriptions',
    label: 'Premium subscription tier',
    description:
      'When on, the Premium tier appears on /billing and existing hosts can upgrade. When off, only the standard tier is visible. Independent of `stripe_live` — toggling them together is the usual move.',
    category: 'commerce',
    recommended: {
      phase_0: 'off',
      phase_waitlist: 'off',
      phase_1: 'founding_cohort',
      phase_2: 'all',
    },
    defaultWhenUnavailable: 'off',
    status: 'stubbed',
    policyNote: 'Off hides Premium CTAs. Already-Premium subscriptions keep their plan.',
    relevantHats: ['host'],
  },
  {
    // Build Brief §1/§4 — the canonical launch switch for the parked Tourer
    // artist tier ($9/mo). OFF at launch. Turning it on brings every Tourer
    // feature to life at once (Smart Match, Smart Touring / Tour Routes, the
    // artist dashboard calendar) — see packages/shared/src/artist-tier-features.ts.
    // The worker's services/artist-tier.ts reads this flag as the platform-level
    // gate; a premium feature stays inert for everyone while it is off, then runs
    // for tier-holders only once it is on. "One switch, clean cascade."
    key: 'artist_tier_tourer',
    label: 'Tourer artist tier',
    description:
      'When on, the $9/mo Tourer tier is live: checkout is reachable and its features (Smart Match, Tour Routes / Smart Touring, the artist dashboard calendar) run for Tourer subscribers. When off, the tier is parked — checkout is refused and every Tourer feature is inert for everyone, regardless of who holds a subscription.',
    category: 'commerce',
    recommended: { phase_0: 'off', phase_waitlist: 'off', phase_1: 'off', phase_2: 'all' },
    defaultWhenUnavailable: 'off',
    status: 'shipped',
    policyNote:
      'Off makes all Tourer features inert and blocks new Tourer checkout. Existing Tourer subscriptions keep their row but their features stop running until the tier is switched back on.',
    relevantHats: ['artist'],
  },
  {
    // Build Brief §1/§4 — launch switch for the parked World Tourer tier
    // ($19/mo). OFF at launch. Gates multi-country tour routes on top of the
    // Tourer feature set.
    key: 'artist_tier_world_tourer',
    label: 'World Tourer artist tier',
    description:
      'When on, the $19/mo World Tourer tier is live: checkout is reachable and multi-country tour routes are permitted for World Tourer subscribers. When off, the tier is parked — checkout is refused and multi-country routing is inert.',
    category: 'commerce',
    recommended: { phase_0: 'off', phase_waitlist: 'off', phase_1: 'off', phase_2: 'all' },
    defaultWhenUnavailable: 'off',
    status: 'shipped',
    policyNote:
      'Off blocks new World Tourer checkout and inert multi-country routing. Existing World Tourer subscriptions keep their row; their multi-country features stop running until switched back on.',
    relevantHats: ['artist'],
  },
  {
    key: 'referral_payouts_live',
    label: 'Referral payouts live',
    description:
      'When on (and STRIPE_SECRET_KEY is bound), the referring-artist revenue-share ledger actually pays out via Stripe Connect transfer, and artists can start Connect onboarding from Settings → Earnings. Accrual into the ledger runs unconditionally regardless of this flag — it moves no money on its own. When off, payouts accrue but never transfer, and Connect onboarding is refused.',
    category: 'commerce',
    recommended: { phase_0: 'off', phase_waitlist: 'off', phase_1: 'off', phase_2: 'off' },
    defaultWhenUnavailable: 'off',
    status: 'stubbed',
    policyNote:
      'Off leaves accrual running but blocks transfer + Connect onboarding.',
    relevantHats: ['artist'],
  },
  {
    key: 'founding_cohort_enrolment',
    label: 'Founding-cohort enrolment',
    description:
      'When on, new venues can claim a founding-cohort position until the cap of 50 is hit. When off, no new positions are issued — useful if Richie wants to pause enrolment for a few days while corralling onboarding.',
    category: 'commerce',
    recommended: { phase_0: 'all', phase_waitlist: 'all', phase_1: 'all', phase_2: 'off' },
    defaultWhenUnavailable: 'off',
    status: 'shipped',
    policyNote:
      'Off blocks new claims. Already-founding venues keep their badge, trial, and grandfathered pricing.',
    relevantHats: ['host'],
  },

  // ─── Discovery ──────────────────────────────────────────────────────────
  {
    key: 'public_browse',
    label: 'Public venue browse',
    description:
      'When on, anonymous visitors can use /browse. When restricted to founding cohort, only founding-cohort users see the map; everyone else is asked to sign in. When off, /browse requires sign-in and shows an empty state for non-cohort users.',
    category: 'discovery',
    recommended: {
      phase_0: 'founding_cohort',
      phase_waitlist: 'founding_cohort',
      phase_1: 'all',
      phase_2: 'all',
    },
    defaultWhenUnavailable: 'all',
    status: 'shipped',
    policyNote:
      'Off forces sign-in. Direct deep-links to /venues/:slug remain accessible because per-venue pages are owned by their venue, not the browse index.',
    relevantHats: ['artist', 'host'],
  },
  {
    key: 'smart_match',
    label: 'Smart Match',
    description:
      'When on, artists see a Smart Match toggle on /browse that auto-fills filters from their profile and scores venues by match strength (art-form overlap, proximity, stipend, sleep, open-week alignment).',
    category: 'discovery',
    recommended: {
      phase_0: 'founding_cohort',
      phase_waitlist: 'founding_cohort',
      phase_1: 'all',
      phase_2: 'all',
    },
    defaultWhenUnavailable: 'off',
    status: 'shipped',
    policyNote:
      'Off hides the toggle; browse reverts to manual filters only. No in-flight state to worry about — Smart Match is a read-only lens over the same venue data.',
    relevantHats: ['artist'],
  },
  {
    key: 'smart_rider_preview',
    label: 'Smart Rider preview',
    description:
      'When on, parties on a confirmed exchange get a branded, generated Smart Rider PDF (load-in, soundcheck, doors, set times, stage, backline, Wi-Fi, hospitality, stay, stipend) auto-populated from the exchange plus an editable per-exchange overrides annex. Off keeps the route + panel dark.',
    category: 'discovery',
    recommended: {
      phase_0: 'off',
      phase_waitlist: 'off',
      phase_1: 'founding_cohort',
      phase_2: 'all',
    },
    defaultWhenUnavailable: 'off',
    status: 'shipped',
    policyNote:
      'Off hides the generate route and client panel entirely (404, not just a UI hide). No in-flight state to worry about — the rider is regenerated fresh from current exchange/venue/profile data on every request.',
    relevantHats: ['artist', 'host'],
  },

  // ─── Communications ─────────────────────────────────────────────────────
  {
    key: 'email_send',
    label: 'Email send',
    description:
      'When on, notifications and password-reset links are dispatched through the email transport. Currently stubbed — the transport itself is not yet attached; messages still log to the worker console.',
    category: 'communications',
    recommended: { phase_0: 'off', phase_waitlist: 'off', phase_1: 'all', phase_2: 'all' },
    defaultWhenUnavailable: 'off',
    status: 'stubbed',
    policyNote:
      'Off keeps the existing console-log behaviour. Already-issued reset tokens stay valid; their delivery just doesn\'t happen via email.',
    relevantHats: ['artist', 'host', 'admin'],
  },
  {
    key: 'web_push',
    label: 'Web push notifications',
    description:
      'When on, the SPA registers the service worker and prompts for push permission. Currently stubbed — DO fanout works in-thread but push-registration is not wired.',
    category: 'communications',
    recommended: { phase_0: 'off', phase_waitlist: 'off', phase_1: 'off', phase_2: 'all' },
    defaultWhenUnavailable: 'off',
    status: 'stubbed',
    policyNote: 'Off skips registration entirely. No in-flight subscriptions to revoke.',
    relevantHats: ['artist', 'host', 'admin'],
  },
  {
    key: 'inbox_attachments',
    label: 'Inbox attachments',
    description:
      'When on, message composers in /inbox show the paperclip and accept up to 4 attachments per message (≤10 MB each). When off, attachments are refused at the API and the paperclip is hidden.',
    category: 'communications',
    recommended: { phase_0: 'all', phase_waitlist: 'all', phase_1: 'all', phase_2: 'all' },
    defaultWhenUnavailable: 'all',
    status: 'shipped',
    policyNote:
      'Off blocks new uploads. Already-sent attachments remain downloadable from existing threads.',
    relevantHats: ['artist', 'host'],
  },
] as const satisfies readonly FeatureFlagDef[];

/** Narrow string-literal union of valid flag keys, derived from FEATURE_FLAGS. */
export type FeatureFlagKey = (typeof FEATURE_FLAGS)[number]['key'];

const FEATURE_FLAG_KEYS: readonly FeatureFlagKey[] = FEATURE_FLAGS.map(
  (f) => f.key,
) as readonly FeatureFlagKey[];

export function isFeatureFlagKey(value: unknown): value is FeatureFlagKey {
  return typeof value === 'string' && (FEATURE_FLAG_KEYS as readonly string[]).includes(value);
}

/** Lookup by key. Returns undefined for unknown keys — caller decides what to do. */
export function getFeatureFlagDef(key: string): FeatureFlagDef | undefined {
  return FEATURE_FLAGS.find((f) => f.key === key);
}

/** All flag keys in registry order — handy for seeding and admin-UI iteration. */
export function allFeatureFlagKeys(): readonly FeatureFlagKey[] {
  return FEATURE_FLAG_KEYS;
}
